import React, { useState, useEffect, forwardRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { CheckboxButton } from "@/components/atoms/Checkbox";
import { Trash2 } from "lucide-react";
import Button from "@/components/atoms/Button";

type ManualInvoiceTableProps = {
  setSubtotal: (subtotal: number) => void;
  currency: string;
  unVisibleColumns: any[];
  onColumnsChange: (unVisibleColumns: any[]) => void;
  isCreditNote?: boolean;
};

type RowData = {
  sowId: string;
  servicePeriod: string;
  serviceType: string;
  description: string;
  rate: string;
  hours: string;
  subAmount: string;
  totalAmount: string;
};

const ManualInvoiceTable = forwardRef<HTMLDivElement, ManualInvoiceTableProps>(
  ({ currency, unVisibleColumns, setSubtotal, onColumnsChange, isCreditNote }, ref) => {
  const initialRow: RowData = {
    sowId: '',
    servicePeriod: '',
    serviceType: '',
    description: '',
    rate: '',
    hours: '',
    subAmount: '',
    totalAmount: ''
  };

  const allColumns = [
    { label: 'Sow ID', value: 'sowId' },
    { label: 'Service Period', value: 'servicePeriod' },
    { label: 'Service Type', value: 'serviceType' },
    { label: 'Rate', value: 'rate' },
    { label: 'Description', value: 'description' },
    { label: 'Hours', value: 'hours' },
    { label: 'Sub-Amount', value: 'subAmount' },
  ];

  const [rows, setRows] = useState<RowData[]>([initialRow]);
  const [visibleColumns, setVisibleColumns] = useState<any[]>(allColumns.filter(column => !unVisibleColumns.includes(column.value)));

  useEffect(() => {
    setVisibleColumns(
      visibleColumns.filter(col => !unVisibleColumns.includes(col))
    );
  }, [unVisibleColumns]);

  const columnOptions = [
    { label: 'Sow ID', value: 'sowId' },
    { label: 'Service Period', value: 'servicePeriod' },
    { label: 'Service Type', value: 'serviceType' },
    { label: 'Rate', value: 'rate' },
    { label: 'Description', value: 'description' },
    { label: 'Hours', value: 'hours' },
    { label: 'Sub-Amount', value: 'subAmount' }
  ];

  const handleAddRow = () => {
    setRows([...rows, { ...initialRow }]);
  };

  const handleDeleteRow = (index: number) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleInputChange = <K extends keyof RowData>(index: number, field: K, value: string | number) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value.toString(); // Ensure the value is a string
    setRows(updatedRows);
  };

  const handleColumnVisibilityChange = (label: string, value: string, isChecked: boolean) => {
    let newColumns = [...visibleColumns];

    if (isChecked) {
      newColumns.push({ label: label, value: value });
      if (label === 'Service Type') {
        
        if (!newColumns.some(newCol => newCol.value === 'rate')) newColumns.push({ label: 'Rate', value: 'rate' });
        if (!newColumns.some(newCol => newCol.value === 'hours')) newColumns.push({ label: 'Hours', value: 'hours' });
        if (!newColumns.some(newCol => newCol.value === 'subAmount')) newColumns.push({ label: 'Sub-Amount', value: 'subAmount' });
      }
    } else {
      newColumns = newColumns.filter(column => column.label !== label);
      if (label === 'Service Type') {
        newColumns = newColumns.filter(column => !["Rate", "Hours", "Sub-Amount"].includes(column.label))
      }
    }

    setVisibleColumns(newColumns);
  };

  useEffect(() => {
    const subtotal = rows.reduce((acc, row) => acc + parseFloat(row.totalAmount || '0'), 0);
    setSubtotal(parseFloat(subtotal.toFixed(2)));
  }, [rows, setSubtotal]);

  useEffect(() => {
    const unVisibleColumns = allColumns.filter(column => !visibleColumns.some(unCol => unCol.value === column.value))

    onColumnsChange(unVisibleColumns);
  }, [visibleColumns]);

  return (
    <div ref={ref} className="w-full flex flex-col items-center mb-4">
      <div className="flex items-center mb-4">
        <Button
          type="button"
          className="mr-4"
          onClick={handleAddRow}
        >
          Add New Row
        </Button>
        <label className="mr-4 font-semibold">Columns Settings:</label>
        <div className="flex gap-4 flex-wrap">
            {columnOptions.map((option) => {
              if (
                !visibleColumns.some(column => column.label === 'Service Type') &&
                ['rate', 'hours', 'subAmount'].includes(option.value)
              ) {
                return null;
              }

              return (
                <CheckboxButton
                  key={option.value}
                  variant="text"
                  className="hover:bg-slate-300 dark:hover:bg-blue rounded-none dark:bg-slate-800"
                  checked={visibleColumns.some(column => column.label === option.label)}
                  onCheckedChange={(checked) =>
                    handleColumnVisibilityChange(option.label, option.value, checked as boolean)
                  }
                >
                  {option.label}
                </CheckboxButton>
              );
            })}
        </div>
      </div>

      <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
        <thead className="border-b-8 border-border">
          <tr>
            {visibleColumns.some(column => column.label === 'Sow ID') && <th className="p-2 text-left">Sow ID</th>}
            {visibleColumns.some(column => column.label === 'Service Period') && <th className="p-2 text-left">Service Period</th>}
            {visibleColumns.some(column => column.label === 'Service Type') && <th className="p-2 text-left">Service Type</th>}
            {visibleColumns.some(column => column.label === 'Rate') && <th className="p-2 text-right">Rate</th>}
            {visibleColumns.some(column => column.label === 'Description') && <th className="p-2 text-left">Description</th>}
            {visibleColumns.some(column => column.label === 'Hours') && <th className="p-2 text-right">Hours</th>}
            {visibleColumns.some(column => column.label === 'Sub-Amount') && <th className="p-2 text-right">Sub-Amount</th>}
            <th className="p-2 text-right">Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b-2 border-border">
              {visibleColumns.some(column => column.label === 'Sow ID') && (
                <td className="p-2 sowId">
                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="Enter Sow ID"
                    value={row.sowId}
                    onChange={(e) => handleInputChange(index, 'sowId', e.target.value)}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Service Period') && (
                <td className="p-2 servicePeriod">
                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="Enter Service Period"
                    value={row.servicePeriod}
                    onChange={(e) => handleInputChange(index, 'servicePeriod', e.target.value)}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Service Type') && (
                <td className="p-2 serviceType">
                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="Enter Service Type"
                    value={row.serviceType}
                    onChange={(e) => handleInputChange(index, 'serviceType', e.target.value)}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Rate') && (
                <td className="p-2 text-right rate">
                  <NumericFormat
                    value={row.rate}
                    className="border p-1 w-full text-right"
                    thousandSeparator=","
                    decimalSeparator="."
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="Enter Rate"
                    onValueChange={(values) => handleInputChange(index, 'rate', values.value)}
                    prefix={`${currency} `}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Description') && (
                <td className="p-2 description">
                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="Enter Description"
                    value={row.description}
                    onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Hours') && (
                <td className="p-2 text-right hours">
                  <NumericFormat
                    value={row.hours}
                    className="border p-1 w-full text-right"
                    thousandSeparator=","
                    decimalSeparator="."
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="Enter Hours"
                    onValueChange={(values) => handleInputChange(index, 'hours', values.value)}
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Sub-Amount') && (
                <td className="p-2 text-right subAmount">
                  <NumericFormat
                    value={row.subAmount}
                    className="border p-1 w-full text-right"
                    thousandSeparator=","
                    decimalSeparator="."
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="Enter Sub-Amount"
                    onValueChange={(values) => handleInputChange(index, 'subAmount', values.value)}
                    prefix={`${isCreditNote ? '-': ''} ${currency} `}
                    allowNegative={false}
                  />
                </td>
              )}
              <td className="p-2 text-right totalAmount">
                <NumericFormat
                  value={row.totalAmount}
                  className="border p-1 w-full text-right"
                  thousandSeparator=","
                  decimalSeparator="."
                  fixedDecimalScale
                  decimalScale={2}
                  placeholder="Enter Total Amount"
                  onValueChange={(values) => handleInputChange(index, 'totalAmount', values.value)}
                  prefix={`${isCreditNote ? '-': ''} ${currency} `}
                  allowNegative={false}
                />
              </td>
              <td className="p-2 text-center delete-button">
                <button onClick={() => handleDeleteRow(index)}>
                  <Trash2 className="text-red-500" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default ManualInvoiceTable;
